import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Storefront, ArrowLeft } from "@phosphor-icons/react";
import api from "../lib/api";
import { ProfileBody } from "./admin/TruckProfilePreview";

/**
 * Public truck profile — `/t/:slug`.
 *
 * Reuses the same <ProfileBody> renderer as the admin preview so there's
 * exactly one source of truth for the layout. The backend returns 404
 * unless `is_published=true`, which we surface as a friendly empty state
 * instead of a console error.
 */
export default function PublicProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    api
      .get(`/profile/${encodeURIComponent(slug)}`)
      .then(({ data }) => {
        if (!mounted) return;
        if (data?.redirect_to) {
          // Old slug — permanent-redirect to the canonical URL.
          navigate(data.redirect_to, { replace: true });
          return;
        }
        setData(data);
      })
      .catch((err) => {
        if (!mounted) return;
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [slug]);

  // Set the SEO <title> (backend og:title carries the full pattern),
  // a plain meta description, and noindex for admin-flagged test profiles.
  useEffect(() => {
    if (data?.og?.["og:title"]) {
      document.title = data.og["og:title"];
    } else if (data?.draft?.truck_name) {
      document.title = `${data.draft.truck_name} Food Truck | Schedule & Locations | TruckTrackr`;
    }
    const added = [];
    if (data?.og?.["og:description"]) {
      const el = document.createElement("meta");
      el.setAttribute("name", "description");
      el.setAttribute("content", data.og["og:description"]);
      document.head.appendChild(el);
      added.push(el);
    }
    if (data?.is_test || notFound) {
      // Test profiles and not-found states must never be indexed —
      // the static host can't emit real 404s for SPA routes, so a
      // noindex soft-404 is the correct signal for crawlers.
      const el = document.createElement("meta");
      el.setAttribute("name", "robots");
      el.setAttribute("content", "noindex, nofollow");
      document.head.appendChild(el);
      added.push(el);
    }
    return () => {
      document.title = "TruckTrackr";
      added.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, [data, notFound]);

  // Inject schema.org JSON-LD so Google can render this profile as a
  // rich result (hours, cuisine, address chip in SERPs). The backend
  // computes the full graph in `/api/profile/{slug}.jsonld` so we just
  // serialize it into a script tag. We mount/unmount with the page so
  // navigating away cleans up the tag.
  useEffect(() => {
    const ld = data?.jsonld;
    if (!ld) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "truck-profile-jsonld";
    try {
      el.textContent = JSON.stringify(ld);
    } catch {
      return undefined;
    }
    document.head.appendChild(el);
    return () => {
      // Defensive: another nav might have already swapped the node.
      const cur = document.getElementById("truck-profile-jsonld");
      if (cur && cur.parentNode) cur.parentNode.removeChild(cur);
    };
  }, [data]);

  // Inject `<link rel="canonical">` pointing at the clean `/t/:slug`
  // URL the backend hands us via og:url. This collapses any share-
  // tracking variants (?utm_source=..., ?ref=...) into a single
  // indexed URL in Google so we don't split link-equity across
  // duplicates. Mounted/unmounted with the page.
  useEffect(() => {
    const canonicalUrl = data?.og?.["og:url"];
    if (!canonicalUrl) return undefined;
    const el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    el.setAttribute("href", canonicalUrl);
    el.setAttribute("data-canonical-injected", "1");
    document.head.appendChild(el);
    return () => {
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
  }, [data]);

  // Inject Open Graph + Twitter Card meta tags so iMessage / WhatsApp /
  // Slack / Facebook / Twitter / LinkedIn render rich preview cards
  // when this URL is shared. Backend computes the dict, we map each
  // entry to a single <meta> element with a stable id so we can clean
  // up on unmount or data refresh without leaving stale tags behind.
  useEffect(() => {
    const og = data?.og;
    if (!og) return undefined;
    const added = [];
    Object.entries(og).forEach(([key, value]) => {
      if (!value) return;
      const el = document.createElement("meta");
      // Twitter convention uses `name`, OG convention uses `property` —
      // some crawlers accept either, but stricter ones (LinkedIn) want
      // the right attribute for each kind. Split on the prefix.
      if (key.startsWith("twitter:")) {
        el.setAttribute("name", key);
      } else {
        el.setAttribute("property", key);
      }
      el.setAttribute("content", String(value));
      el.setAttribute("data-og-injected", "1");
      document.head.appendChild(el);
      added.push(el);
    });
    return () => {
      added.forEach((el) => {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-dvh bg-background">
        <div className="h-56 sm:h-80 lg:h-96 w-full bg-muted animate-pulse" />
        <div className="max-w-2xl mx-auto px-4 mt-4">
          <div className="flex items-center gap-3">
            <div className="h-20 w-20 rounded-2xl border border-border bg-muted animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-8 w-2/3 bg-muted rounded animate-pulse" />
              <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="max-w-md mx-auto p-8 text-center" data-testid="public-profile-not-found">
        <Storefront weight="duotone" className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
        <h1 className="font-display font-bold text-xl mb-2">Profile not found</h1>
        <p className="text-sm text-muted-foreground mb-4">
          This truck doesn&rsquo;t have a public profile yet, or it may have been taken down.
        </p>
        <Link
          to="/map"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          data-testid="public-profile-find-trucks"
        >
          <ArrowLeft weight="bold" className="h-4 w-4" />
          Find more trucks
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Public-facing only — small sticky CTA encouraging customers
          to subscribe their calendar to events from trucks they follow.
          Hidden on the admin preview render. */}
      <div
        className="border-b border-border bg-primary/5"
        data-testid="public-profile-calendar-cta"
      >
        <div className="max-w-2xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
          <p className="text-[12px] sm:text-sm text-muted-foreground">
            <span className="hidden sm:inline">📅 </span>
            Want this truck&apos;s events on your calendar?
          </p>
          <Link
            to="/calendar-sync"
            className="text-xs font-semibold text-primary hover:underline whitespace-nowrap"
            data-testid="public-profile-calendar-link"
          >
            Set up calendar sync →
          </Link>
        </div>
      </div>
      <ProfileBody
        data={data}
        showAllEvents={showAllEvents}
        onToggleEvents={() => setShowAllEvents((v) => !v)}
        embedded={false}
        isPublic
      />
    </>
  );
}
